import { useCallback, useEffect, useState } from 'react';
import {
  Page,
  Card,
  FormLayout,
  TextField,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Checkbox,
  Banner,
  RangeSlider,
  ButtonGroup,
} from '@shopify/polaris';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../../providers/ApiProvider';
import {
  DEFAULT_MILESTONE_DESIGN,
  toColorInputValue,
  type MilestoneDesign,
} from './milestoneDefaults';
import { MilestonePreview } from './MilestonePreview';
import {
  ContextualSaveBar,
  SavingOverlay,
  isShopifyEmbedded,
  showAppToast,
  useUnsavedChanges,
} from '../../components/ContextualSaveBar';
import { ThemeColorRefresh } from '../../components/ThemeColorRefresh';
import { t } from '../../i18n';

function ColorField({
  label,
  value,
  onChange,
  helpText,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  helpText?: string;
  disabled?: boolean;
}) {
  const pickerValue = toColorInputValue(value, '#000000');
  return (
    <div>
      <Text as="p" variant="bodyMd">
        {label}
      </Text>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginTop: 6,
        }}
      >
        <input
          type="color"
          value={pickerValue}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
          style={{
            width: 40,
            height: 36,
            padding: 0,
            border: '1px solid #c9cccf',
            borderRadius: 8,
            background: 'transparent',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
          }}
        />
        <div style={{ flex: 1 }}>
          <TextField
            label={label}
            labelHidden
            value={value}
            onChange={onChange}
            autoComplete="off"
            disabled={disabled}
            helpText={helpText}
          />
        </div>
      </div>
    </div>
  );
}

export function MilestoneEditorPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return') || '/widgets';
  const forcePreview = searchParams.get('preview') === '1';

  const [design, setDesign] = useState<MilestoneDesign>({
    ...DEFAULT_MILESTONE_DESIGN,
  });
  const [widgetId, setWidgetId] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>(
    'desktop',
  );
  const [detailsOpen, setDetailsOpen] = useState(forcePreview);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [productBlock, setProductBlock] = useState<{
    active: boolean;
    deepLink: string;
  } | null>(null);
  const embedded = isShopifyEmbedded();
  const { dirty, markClean, getBaseline, markCleanAfterDiscard } =
    useUnsavedChanges(design);

  const patch = useCallback(
    <K extends keyof MilestoneDesign>(key: K, value: MilestoneDesign[K]) => {
      setDesign((d) => ({ ...d, [key]: value }));
    },
    [],
  );

  useEffect(() => {
    api
      .get<{ active: boolean; deepLink: string }>('/stores/theme/product-block')
      .then((r) =>
        setProductBlock({
          active: Boolean(r.active),
          deepLink: r.deepLink || '#',
        }),
      )
      .catch(() => setProductBlock(null));
  }, [api]);

  useEffect(() => {
    setLoading(true);
    api
      .get<
        Array<{
          id: string;
          type: string;
          campaignId?: string | null;
          updatedAt?: string;
          content?: Record<string, unknown>;
          styles?: Record<string, unknown>;
        }>
      >('/widgets')
      .then((list) => {
        const bars = (list || []).filter((w) => w.type === 'PROGRESS_BAR');
        // Same rule as API: most recently updated PROGRESS_BAR (the one we save)
        bars.sort((a, b) => {
          const ta = a.updatedAt ? Date.parse(a.updatedAt) : 0;
          const tb = b.updatedAt ? Date.parse(b.updatedAt) : 0;
          return tb - ta;
        });
        const ms = bars[0] || null;
        if (!ms) {
          const defaults = { ...DEFAULT_MILESTONE_DESIGN };
          setDesign(defaults);
          markClean(defaults);
          return;
        }
        setWidgetId(ms.id);
        const c = ms.content || {};
        const s = ms.styles || {};
        const next: MilestoneDesign = {
          ...DEFAULT_MILESTONE_DESIGN,
          fallbackTitle: String(
            c.fallbackTitle ?? c.title ?? DEFAULT_MILESTONE_DESIGN.fallbackTitle,
          ),
          detailsLabel: String(
            c.detailsLabel ?? DEFAULT_MILESTONE_DESIGN.detailsLabel,
          ),
          detailsTitle: String(
            c.detailsTitle ?? DEFAULT_MILESTONE_DESIGN.detailsTitle,
          ),
          accentColor: String(
            s.accentColor ?? DEFAULT_MILESTONE_DESIGN.accentColor,
          ),
          lineColor: String(s.lineColor ?? DEFAULT_MILESTONE_DESIGN.lineColor),
          backgroundColor: String(
            s.backgroundColor ?? DEFAULT_MILESTONE_DESIGN.backgroundColor,
          ),
          textColor: String(s.textColor ?? DEFAULT_MILESTONE_DESIGN.textColor),
          mutedColor: String(
            s.mutedColor ?? DEFAULT_MILESTONE_DESIGN.mutedColor,
          ),
          borderRadius: Number(
            s.borderRadius ?? DEFAULT_MILESTONE_DESIGN.borderRadius,
          ),
          maxWidth: Number(s.maxWidth ?? DEFAULT_MILESTONE_DESIGN.maxWidth),
          showChip: s.showChip !== false,
          showDetails: s.showDetails !== false,
          fontFamily: String(
            s.fontFamily ?? DEFAULT_MILESTONE_DESIGN.fontFamily,
          ),
          autoFitTheme: s.autoFitTheme !== false,
          themeSchemeId:
            typeof s.themeSchemeId === 'string' ? s.themeSchemeId : null,
          detailsBackgroundColor: String(
            s.detailsBackgroundColor ??
              DEFAULT_MILESTONE_DESIGN.detailsBackgroundColor,
          ),
          detailsTextColor: String(
            s.detailsTextColor ?? DEFAULT_MILESTONE_DESIGN.detailsTextColor,
          ),
          detailsMutedColor: String(
            s.detailsMutedColor ?? DEFAULT_MILESTONE_DESIGN.detailsMutedColor,
          ),
          detailsBorderColor: String(
            s.detailsBorderColor ?? DEFAULT_MILESTONE_DESIGN.detailsBorderColor,
          ),
          detailsItemBackgroundColor: String(
            s.detailsItemBackgroundColor ??
              DEFAULT_MILESTONE_DESIGN.detailsItemBackgroundColor,
          ),
          detailsAccentColor: String(
            s.detailsAccentColor ??
              s.accentColor ??
              DEFAULT_MILESTONE_DESIGN.detailsAccentColor,
          ),
        };
        setDesign(next);
        markClean(next);
      })
      .catch(() => {
        const defaults = { ...DEFAULT_MILESTONE_DESIGN };
        setDesign(defaults);
        markClean(defaults);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  async function save() {
    setSaving(true);
    setError(null);
    setMessage(null);
    // Snapshot current design so Save bar never posts a stale closure
    const snap = { ...design };
    try {
      const payload = {
        name: 'Milestone progress',
        type: 'PROGRESS_BAR' as const,
        placement: 'PRODUCT' as const,
        content: {
          fallbackTitle: snap.fallbackTitle,
          title: snap.fallbackTitle,
          detailsLabel: snap.detailsLabel,
          detailsTitle: snap.detailsTitle,
        },
        styles: {
          accentColor: snap.accentColor,
          lineColor: snap.lineColor,
          // Aliases so older readers / any path still pick up progress line
          progressLineColor: snap.lineColor,
          line_color: snap.lineColor,
          backgroundColor: snap.backgroundColor,
          textColor: snap.textColor,
          mutedColor: snap.mutedColor,
          borderRadius: snap.borderRadius,
          maxWidth: snap.maxWidth,
          showChip: snap.showChip,
          showDetails: snap.showDetails,
          fontFamily: snap.fontFamily,
          autoFitTheme: snap.autoFitTheme,
          themeSchemeId: snap.themeSchemeId,
          detailsBackgroundColor: snap.detailsBackgroundColor,
          detailsTextColor: snap.detailsTextColor,
          detailsMutedColor: snap.detailsMutedColor,
          detailsBorderColor: snap.detailsBorderColor,
          detailsItemBackgroundColor: snap.detailsItemBackgroundColor,
          detailsAccentColor: snap.detailsAccentColor,
        },
        layout: { position: 'custom', fullWidth: false },
        animations: { enter: 'fade', durationMs: 200 },
        isEnabled: true,
      };

      if (widgetId) {
        await api.patch(`/widgets/${widgetId}`, payload);
      } else {
        const created = await api.post<{ id: string }>('/widgets', payload);
        setWidgetId(created.id);
      }
      await api
        .patch('/stores/setup', { widgetCustomized: true })
        .catch(() => undefined);
      markClean(snap);
      setMessage(
        `Milestone saved. Progress line: ${snap.lineColor}. Hard-refresh the product page to see it.`,
      );
      showAppToast(`Saved · line ${snap.lineColor}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setError(msg);
      showAppToast(msg, { isError: true });
    } finally {
      setSaving(false);
    }
  }

  function discard() {
    const base = getBaseline();
    if (!base) return;
    setDesign(base);
    setError(null);
    setMessage(null);
    markCleanAfterDiscard(base);
  }

  return (
    <Page
      title={t("Edit milestone progress")}
      subtitle={t("Product page block · tiered rewards progress bar")}
      backAction={{
        content: t("Back"),
        onAction: () => navigate(returnTo),
      }}
      primaryAction={
        !embedded
          ? {
              content: t("Save widget"),
              loading: saving,
              disabled: !dirty,
              onAction: () => void save(),
            }
          : undefined
      }
      secondaryActions={[
        {
          content: t("Reset defaults"),
          onAction: () => setDesign({ ...DEFAULT_MILESTONE_DESIGN }),
        },
      ]}
    >
      <ContextualSaveBar
        id="freebi-milestone-save"
        open={embedded && dirty && !loading}
        saving={saving}
        onSave={save}
        onDiscard={discard}
        saveLabel="Save"
      />
      <SavingOverlay open={saving} label={t("Saving milestone widget…")} />
      <BlockStack gap="400">
        {message && (
          <Banner tone="success" onDismiss={() => setMessage(null)}>
            {message}
          </Banner>
        )}
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        {loading && (
          <Banner tone="info">
            <p>{t("Loading saved milestone widget…")}</p>
          </Banner>
        )}

        <Banner tone="info">
          <p>
            {t("This designs the")} <strong>{t("Milestone progress")}</strong> {t("product block.\n            Ideal placement is")} <strong>{t("just below the Buy button")}</strong> {t("on the\n            product template. If the block is not in the theme yet, Freebi\n            also auto-places it under Buy when the app embed is on and a\n            milestone campaign is active.")}
          </p>
        </Banner>

        {productBlock && !productBlock.active && productBlock.deepLink !== '#' && (
          <Banner
            tone="warning"
            title={t("Add block under Buy (recommended)")}
            action={{
              content: t("Open theme editor"),
              onAction: () => {
                window.open(
                  productBlock.deepLink,
                  '_blank',
                  'noopener,noreferrer',
                );
              },
            }}
          >
            <p>
              {t("Opens the product template with")} <strong>{t("Freebi Milestone\n              Progress")}</strong> {t("ready to add. Place it directly under the Buy\n              buttons, then save the theme. Auto-placement still works without\n              this if the storefront app embed is enabled.")}
            </p>
          </Banner>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(280px, 380px) minmax(0, 1fr)',
            gap: 16,
            alignItems: 'start',
          }}
        >
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                {t("Content")}
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                {t("Fallback title when cart is empty or progress copy isn’t set.\n                Campaigns still control per-milestone reward labels.")}
              </Text>
              <FormLayout>
                <TextField
                  label={t("Fallback title")}
                  value={design.fallbackTitle}
                  onChange={(v) => patch('fallbackTitle', v)}
                  autoComplete="off"
                  helpText={t("Shown until progress text loads")}
                />
                <TextField
                  label={t("Details button label")}
                  value={design.detailsLabel}
                  onChange={(v) => patch('detailsLabel', v)}
                  autoComplete="off"
                />
                <TextField
                  label={t("Details panel title")}
                  value={design.detailsTitle}
                  onChange={(v) => patch('detailsTitle', v)}
                  autoComplete="off"
                />
                <Checkbox
                  label={t("Show bottom chip banner")}
                  checked={design.showChip}
                  onChange={(v) => patch('showChip', v)}
                />
                <Checkbox
                  label={t("Allow See details panel")}
                  checked={design.showDetails}
                  onChange={(v) => patch('showDetails', v)}
                />
              </FormLayout>

              <Text as="h2" variant="headingMd">
                {t("Auto-fit with your theme")}
              </Text>
              <Banner tone="info">
                <p>
                  {t("When enabled, the storefront samples button and text colors\n                  from the live theme so the block matches your brand.")}
                </p>
              </Banner>
              <Checkbox
                label={t("Auto-fit with your theme")}
                checked={design.autoFitTheme}
                onChange={(v) => patch('autoFitTheme', v)}
                helpText={t("Uses your theme’s primary colors on the storefront")}
              />
              <ThemeColorRefresh
                onApply={(c) => {
                  setDesign((d) => ({
                    ...d,
                    autoFitTheme: true,
                    themeSchemeId: c.schemeId || d.themeSchemeId,
                    accentColor: c.accentColor || d.accentColor,
                    lineColor: c.lineColor || c.accentColor || d.lineColor,
                    backgroundColor: c.backgroundColor || d.backgroundColor,
                    textColor: c.textColor || d.textColor,
                    mutedColor: c.mutedColor || d.mutedColor,
                  }));
                }}
              />

              <Text as="h2" variant="headingMd">
                {t("Style")}
              </Text>
              {design.autoFitTheme && (
                <Banner tone="warning">
                  <p>
                    {t("Manual colors are saved as fallbacks but won’t apply while\n                    Auto-fit is on.")}
                  </p>
                </Banner>
              )}
              <FormLayout>
                <ColorField
                  label={t("Accent / unlocked nodes")}
                  value={design.accentColor}
                  onChange={(v) => patch('accentColor', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Progress line")}
                  value={design.lineColor}
                  onChange={(v) => patch('lineColor', v)}
                  helpText={t("Track fill color. Saved independently of Auto-fit / accent.")}
                />
                <ColorField
                  label={t("Card background")}
                  value={design.backgroundColor}
                  onChange={(v) => patch('backgroundColor', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Text color")}
                  value={design.textColor}
                  onChange={(v) => patch('textColor', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Muted labels")}
                  value={design.mutedColor}
                  onChange={(v) => patch('mutedColor', v)}
                  disabled={design.autoFitTheme}
                />
                <RangeSlider
                  label={`Corner radius (${design.borderRadius}px)`}
                  value={design.borderRadius}
                  min={0}
                  max={28}
                  onChange={(v) =>
                    patch('borderRadius', Array.isArray(v) ? v[0] : v)
                  }
                  output
                />
                <RangeSlider
                  label={`Max width (${design.maxWidth}px)`}
                  value={design.maxWidth}
                  min={320}
                  max={720}
                  onChange={(v) =>
                    patch('maxWidth', Array.isArray(v) ? v[0] : v)
                  }
                  output
                />
              </FormLayout>

              <Text as="h2" variant="headingMd">
                {t("Details panel style")}
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                {t("Colors for the open “See details” tier list only. These stay\n                independent of Auto-fit theme colors on the progress bar.")}
              </Text>
              <FormLayout>
                <ColorField
                  label={t("Panel background")}
                  value={design.detailsBackgroundColor}
                  onChange={(v) => patch('detailsBackgroundColor', v)}
                />
                <ColorField
                  label={t("Panel text")}
                  value={design.detailsTextColor}
                  onChange={(v) => patch('detailsTextColor', v)}
                />
                <ColorField
                  label={t("Panel muted text")}
                  value={design.detailsMutedColor}
                  onChange={(v) => patch('detailsMutedColor', v)}
                />
                <ColorField
                  label={t("Panel border")}
                  value={design.detailsBorderColor}
                  onChange={(v) => patch('detailsBorderColor', v)}
                />
                <ColorField
                  label={t("Tier row background")}
                  value={design.detailsItemBackgroundColor}
                  onChange={(v) => patch('detailsItemBackgroundColor', v)}
                />
                <ColorField
                  label={t("Details accent (unlocked / next)")}
                  value={design.detailsAccentColor}
                  onChange={(v) => patch('detailsAccentColor', v)}
                  helpText={t("Badges and status colors inside the open details list")}
                />
              </FormLayout>

              {!embedded && (
                <Button
                  variant="primary"
                  loading={saving}
                  disabled={!dirty}
                  onClick={() => void save()}
                >
                  {t("Save widget")}
                </Button>
              )}
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  {t("Live preview")}
                </Text>
                <ButtonGroup variant="segmented">
                  <Button
                    pressed={previewMode === 'desktop'}
                    onClick={() => setPreviewMode('desktop')}
                  >
                    {t("Desktop")}
                  </Button>
                  <Button
                    pressed={previewMode === 'mobile'}
                    onClick={() => setPreviewMode('mobile')}
                  >
                    {t("Mobile")}
                  </Button>
                </ButtonGroup>
              </InlineStack>
              <InlineStack gap="200">
                <Button
                  size="slim"
                  pressed={!detailsOpen}
                  onClick={() => setDetailsOpen(false)}
                >
                  {t("Bar only")}
                </Button>
                <Button
                  size="slim"
                  pressed={detailsOpen}
                  onClick={() => setDetailsOpen(true)}
                >
                  {t("Details open")}
                </Button>
              </InlineStack>
              <Text as="p" tone="subdued" variant="bodySm">
                {t("Matches the product-page Milestone progress block shoppers see.")}
              </Text>
              <div
                style={{
                  maxWidth: previewMode === 'mobile' ? 360 : '100%',
                  margin: '0 auto',
                }}
              >
                <MilestonePreview
                  design={design}
                  mode={previewMode}
                  detailsOpen={detailsOpen}
                  progressPercent={28}
                />
              </div>
            </BlockStack>
          </Card>
        </div>
      </BlockStack>
    </Page>
  );
}
